import { useIsClient } from '@/core/hooks/use-is-client'
import { useMutation } from '@/core/hooks/use-mutation'
import { dateService } from '@/core/services/date'
import { getBookFormValidation } from '@/features/reservation/form-validation/book-form.validation'
import {
  bookFormInitialState,
  useReservationStore,
} from '@/features/reservation/store'
import { useRouter } from '@/i18n/navigation'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { getTripInfo } from '../../providers/client'
import { useOrderSummaryStore } from '../../store'
import { OrderStep } from '../../types/enums/order-step.enum'
import { BookFormModel } from '../../types/models/book-form.model'
import { TripInfoModel } from '../../types/models/trip-info.model'

type Params = {
  initialValues?: BookFormModel
  nextStep?: string
  onSuccess?: () => void
}

export const useBookForm = ({ initialValues, nextStep, onSuccess }: Params) => {
  const t = useTranslations()
  const isClient = useIsClient()
  const { schema } = getBookFormValidation(t)

  const resetReservation = useReservationStore(state => state.reset)
  const setTripInfo = useReservationStore(state => state.setTripInfo)
  const setBookForm = useReservationStore(state => state.setBookForm)
  const setCountry = useReservationStore(state => state.setCountry)
  const setOrderSummary = useOrderSummaryStore(state => state.setMany)
  const router = useRouter()
  const defaultValues = initialValues || bookFormInitialState
  const hasInitialValue = Boolean(initialValues)

  const { isPending, error, mutate } = useMutation<
    BookFormModel,
    TripInfoModel
  >({
    mutation: getTripInfo,
    onSuccess(result, sentData) {
      setTripInfo(result)
      setBookForm(sentData)
      setOrderSummary({
        vehicleSizeName: result.vehicleSize.name,
        price: result.price,
        totalPrice: result.totalPrice,
        discount: result.discount,
        currency: result.currency,
        step: OrderStep.CREATE_TRIP,
      })
      onSuccess?.()
      if (nextStep) router.push(nextStep)
    },
  })

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onTouched',
    disabled: !isClient,
  })

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues)
      setBookForm(defaultValues)
      if (defaultValues.country) {
        setCountry(defaultValues.country)
      }
    }
  }, [form, defaultValues, setBookForm, setCountry])

  useEffect(() => {
    if (!hasInitialValue) {
      resetReservation()
    }
  }, [resetReservation, hasInitialValue])

  const onSubmit = form.handleSubmit(values => {
    setOrderSummary({ ...values })
    const sentValues = {
      ...values,
      date:
        values.date instanceof Date
          ? dateService.dateToYyMmDd(values.date)
          : values.date,
      time:
        values.time instanceof Date
          ? dateService.timeToHI(values.time)
          : values.time,
    }
    mutate(sentValues)
  })

  return {
    form,
    onSubmit,
    error,
    errors: form.formState.errors,
    isPending,
  }
}
